import type { SelectBaseOption, SelectOptionItem } from './common';

const matchesSearch = (option: SelectBaseOption, searchLower: string): boolean => {
  const labelMatch =
    typeof option.label === 'string' && option.label.toLowerCase().includes(searchLower);
  const searchLabelMatch = option.searchLabel?.toLowerCase().includes(searchLower);
  const valueMatch = option.value.toLowerCase().includes(searchLower);

  return labelMatch || searchLabelMatch || valueMatch;
};

export const filterSelectOptionsByValueOrSearchLabel = (
  options: SelectOptionItem[],
  searchValue: string,
): SelectOptionItem[] => {
  if (!searchValue.trim()) {
    return options;
  }

  const searchLower = searchValue.toLowerCase();

  return options
    .map((option) => {
      if (option.type === 'group') {
        const filteredSubOptions = option.options.filter((subOption) =>
          matchesSearch(subOption, searchLower),
        );

        return filteredSubOptions.length > 0 ? { ...option, options: filteredSubOptions } : null;
      }

      if (option.type === 'separator') {
        return option;
      }

      return matchesSearch(option, searchLower) ? option : null;
    })
    .filter((option): option is SelectOptionItem => option !== null);
};

export const filterSelectOptionForSearch = (options: SelectOptionItem[]): SelectBaseOption[] =>
  options.filter((option) => {
    return option.type !== 'group' && option.type !== 'separator';
  }) as SelectBaseOption[];

export const getOptionLabelByValue = (
  options: SelectOptionItem[],
  value: string,
): React.ReactNode => {
  for (const option of options) {
    if (option.type === 'group') {
      const found = option.options.find((subOption) => subOption.value === value);
      if (found) {
        return formatOptionLabel(found);
      }
    } else if (option.type !== 'separator' && option.value === value) {
      return formatOptionLabel(option);
    }
  }
  return undefined;
};

export const getFlattenedOptions = (options: SelectOptionItem[]): SelectBaseOption[] => {
  const flattened: SelectBaseOption[] = [];

  options.forEach((option) => {
    if (option.type === 'group') {
      flattened.push(...option.options);
    } else if (option.type !== 'separator') {
      flattened.push(option);
    }
  });

  return flattened;
};

export const hasSelectedValues = (value: string | string[] | undefined): boolean => {
  return Array.isArray(value) ? value.length > 0 : Boolean(value);
};

export const toggleValueInArray = (
  currentArray: string[],
  value: string,
  maxSelected?: number,
): string[] => {
  if (currentArray.includes(value)) {
    return currentArray.filter((v) => v !== value);
  }

  if (maxSelected && currentArray.length >= maxSelected) {
    return currentArray;
  }

  return [...currentArray, value];
};

export const isValidSelectOption = (option: any): option is SelectOptionItem => {
  if (!option || typeof option !== 'object') {
    return false;
  }

  if (option.type === 'separator') {
    return true;
  }

  if (option.type === 'group') {
    return (
      typeof option.groupLabel === 'string' &&
      Array.isArray(option.options) &&
      option.options.every((subOption: any) => isValidSelectOption(subOption))
    );
  }

  return (
    typeof option.value === 'string' &&
    typeof option.label === 'string' &&
    (option.disabled === undefined || typeof option.disabled === 'boolean')
  );
};

export const formatOptionLabel = (option: SelectBaseOption): React.ReactNode => {
  const labelToUse = option.valueLabel ?? option.label;

  return typeof labelToUse === 'string' ? labelToUse.trim().replace(/\u2800/g, '') : labelToUse;
};
